import { NavLink, Outlet } from 'react-router-dom';
import { Button, cx } from '../../components/ui';
import { useAdminAuth } from '../../lib/adminAuth';

/**
 * The portal shell.
 *
 * Deliberately **outside** the marketing `Layout`. An operator suspending an
 * account should not be looking at a "Start free trial" call to action, and the
 * public header would put one there. The visual language is the same brand, the
 * furniture is not.
 */

const PANELS = [
  { to: '/admin', label: 'Dashboard', end: true, icon: '▦' },
  { to: '/admin/users', label: 'Users', icon: '◍' },
  { to: '/admin/subscriptions', label: 'Subscriptions', icon: '◈' },
  { to: '/admin/revenue', label: 'Revenue', icon: '↗' },
  { to: '/admin/plans', label: 'Pricing Plans', icon: '◇' },
  { to: '/admin/promotions', label: 'Promotions', icon: '◎' },
  { to: '/admin/settings', label: 'Settings', icon: '⚙' },
] as const;

export function AdminLayout() {
  const { admin, signOut } = useAdminAuth();

  return (
    <div className="min-h-screen bg-hull-950 lg:flex">
      <aside className="border-b border-hull-800 bg-hull-900 lg:w-60 lg:shrink-0 lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between px-5 py-5 lg:block">
          <div>
            <p className="text-sm font-semibold tracking-[0.2em] text-white">WAVEQA</p>
            <p className="mt-0.5 text-xs text-surge-400">Admin Portal</p>
          </div>
        </div>

        <nav aria-label="Admin" className="px-3 pb-4">
          <ul className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
            {PANELS.map((panel) => (
              <li key={panel.to}>
                <NavLink
                  to={panel.to}
                  end={'end' in panel ? panel.end : false}
                  className={({ isActive }) =>
                    cx(
                      'flex items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition',
                      isActive
                        ? 'bg-hull-800 font-medium text-white'
                        : 'text-hull-300 hover:bg-hull-800/60 hover:text-white',
                    )
                  }
                >
                  <span aria-hidden="true" className="text-surge-400">
                    {panel.icon}
                  </span>
                  {panel.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="min-w-0 flex-1">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-hull-800 px-5 py-4 sm:px-8">
          <p className="min-w-0 truncate text-sm text-hull-400">
            Signed in as <span className="text-hull-200">{admin?.email}</span>
            {admin && !admin.mfa_enabled && (
              // Worth saying on every screen: an administrator without a second
              // factor is the account an attacker wants, and the person who can
              // switch it back on is a colleague, not them.
              <span className="ml-2 rounded-full border border-warn-600/40 bg-warn-600/10 px-2 py-0.5 text-xs text-warn-400">
                Two-factor off
              </span>
            )}
          </p>
          <Button variant="secondary" onClick={() => void signOut()}>
            Sign out
          </Button>
        </header>

        <main className="px-5 py-8 sm:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Every panel state in one place, matching the customer dashboard's contract. */
export function AdminPanelState({
  loading,
  error,
  empty,
  emptyTitle,
  emptyBody,
  children,
}: {
  loading: boolean;
  error: unknown;
  empty?: boolean;
  emptyTitle?: string;
  emptyBody?: string;
  children: React.ReactNode;
}) {
  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        {[0, 1, 2].map((row) => (
          <div key={row} className="h-24 animate-pulse rounded-card bg-hull-800/60" />
        ))}
      </div>
    );
  }

  if (error) {
    const message = error instanceof Error ? error.message : 'Something went wrong.';
    return (
      <div role="alert" className="rounded-card border border-fail-600/40 bg-fail-600/10 p-6">
        <h2 className="text-base font-semibold text-fail-400">Could not load this panel</h2>
        <p className="mt-2 text-sm text-hull-300">{message}</p>
      </div>
    );
  }

  if (empty) {
    return (
      <div className="rounded-card border border-dashed border-hull-700 p-10 text-center">
        <h2 className="text-base font-semibold text-white">{emptyTitle}</h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-hull-400">{emptyBody}</p>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * A headline figure with its movement.
 *
 * `change` is null when there is no honest comparison — an empty preceding
 * window, or a snapshot metric with no history — and the badge is then omitted
 * entirely rather than rendered as 0%, which would assert flat growth.
 */
export function StatTile({
  label,
  value,
  change,
  hint,
}: {
  label: string;
  value: string;
  change?: number | null;
  hint?: string;
}) {
  const rising = change !== null && change !== undefined && change > 0;
  const falling = change !== null && change !== undefined && change < 0;

  return (
    <div className="rounded-card border border-hull-700 bg-hull-800/60 p-5">
      <p className="text-xs font-medium uppercase tracking-[0.14em] text-hull-400">{label}</p>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-white sm:text-3xl">{value}</p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {change !== null && change !== undefined && (
          <span
            className={cx(
              'rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
              rising && 'bg-pass-600/15 text-pass-400',
              falling && 'bg-fail-600/15 text-fail-400',
              !rising && !falling && 'bg-hull-700/60 text-hull-300',
            )}
          >
            {rising ? '+' : ''}
            {change}%
          </span>
        )}
        {hint && <span className="text-xs text-hull-400">{hint}</span>}
      </div>
    </div>
  );
}
